import { useState, useEffect, useRef } from "react"
import { Button, Flex, Input, InputNumber, Modal, Select, TimePicker, Tooltip } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { Text } from "ui/Typography"
import CustomDrawer from "ui/Drawer"
import { useDevice } from "hooks/useDevice"
import styles from "./AddAvailabilityScheduleModal.module.css"

const DAYS = [
  { key: "sunday", label: "Sun" },
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
]

const INTERVAL_OPTIONS = [
  { value: 15, label: "15 min" },
  { value: 30, label: "30 min" },
  { value: 60, label: "1 hour" },
  { value: 120, label: "2 hours" },
]

const generateTimes = (from, to, intervalMinutes) => {
  if (!from || !to || !intervalMinutes) return []
  const times = []
  let current = from.clone()
  // "To" is the closing boundary, not a start slot — an experience can't begin at the
  // moment availability ends. So the last start is strictly before To (e.g. 8AM–6PM
  // with 30-min steps ends at 5:30 PM, not 6:00 PM).
  while (current.isBefore(to)) {
    times.push(current.format("hh:mm A"))
    current = current.add(intervalMinutes, "minute")
  }
  return times
}

export const AddAvailabilityScheduleModal = ({ open, onClose, onSave, existingSchedules = [], editingSchedule = null }) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const isEditing = !!editingSchedule

  const [name, setName] = useState("")
  const [selectedDays, setSelectedDays] = useState([])
  const [fromTime, setFromTime] = useState(null)
  const [toTime, setToTime] = useState(null)
  const [interval, setInterval] = useState(null)
  const [isIntervalOpen, setIsIntervalOpen] = useState(false)
  const [customHour, setCustomHour] = useState(undefined)
  const [customMinute, setCustomMinute] = useState(undefined)
  const [customError, setCustomError] = useState("")
  // previewTimes: string[] — full list of generated times
  // disabledTimes: Set<string> — user-toggled off times
  const [previewTimes, setPreviewTimes] = useState([])
  const [disabledTimes, setDisabledTimes] = useState(new Set())
  // On edit, carries the saved schedule's disabled state into the regenerate
  // effect so the struck-out chips can be restored. Prefer the explicit
  // disabled_times list; fall back to (generated − enabled start_times) for
  // older records that only stored the enabled list. Consumed once per
  // edit-open, then cleared so later user edits reset cleanly.
  const editDisabledRef = useRef(null)
  const editEnabledRef = useRef(null)

  useEffect(() => {
    if (!open) return
    if (editingSchedule) {
      setName(editingSchedule.name || "")
      setSelectedDays(editingSchedule.days || [])
      setFromTime(editingSchedule.from_time ? dayjs(editingSchedule.from_time, "HH:mm") : null)
      setToTime(editingSchedule.to_time ? dayjs(editingSchedule.to_time, "HH:mm") : null)
      setInterval(editingSchedule.interval || null)
      // Remember the saved disabled state; the regenerate effect rebuilds the
      // full grid and re-applies the struck-out chips from it.
      editDisabledRef.current = editingSchedule.disabled_times || null
      editEnabledRef.current = editingSchedule.start_times || []
      setPreviewTimes(editingSchedule.start_times || [])
      setDisabledTimes(new Set())
    } else {
      setName("")
      setSelectedDays([])
      setFromTime(null)
      setToTime(null)
      setInterval(null)
      editDisabledRef.current = null
      editEnabledRef.current = null
      setPreviewTimes([])
      setDisabledTimes(new Set())
    }
    setCustomHour(undefined)
    setCustomMinute(undefined)
    setCustomError("")
  }, [open, editingSchedule])

  // Auto-generate preview whenever from/to/interval change
  useEffect(() => {
    if (!fromTime || !toTime || !interval) {
      setPreviewTimes([])
      setDisabledTimes(new Set())
      editDisabledRef.current = null
      editEnabledRef.current = null
      return
    }
    const generated = generateTimes(fromTime, toTime, interval)
    setPreviewTimes(generated)
    if (editDisabledRef.current) {
      // Edit load: restore the saved disabled chips, scoped to the current grid.
      const savedDisabled = new Set(editDisabledRef.current)
      setDisabledTimes(new Set(generated.filter((time) => savedDisabled.has(time))))
    } else if (editEnabledRef.current) {
      // Fallback for older records: anything in the grid that wasn't a saved
      // enabled time is a previously-disabled slot.
      const savedEnabled = new Set(editEnabledRef.current)
      setDisabledTimes(new Set(generated.filter((time) => !savedEnabled.has(time))))
    } else {
      setDisabledTimes(new Set())
    }
    editDisabledRef.current = null
    editEnabledRef.current = null
  }, [fromTime, toTime, interval])

  const takenDays = new Set(
    existingSchedules
      .filter((s) => !editingSchedule || s.id !== editingSchedule.id)
      .flatMap((s) => s.days || [])
  )

  const handleDayToggle = (dayKey) => {
    // Days taken by another schedule are disabled — ignore clicks defensively.
    if (takenDays.has(dayKey)) return
    setSelectedDays((prev) =>
      prev.includes(dayKey) ? prev.filter((day) => day !== dayKey) : [...prev, dayKey]
    )
  }

  const handleToggleTime = (time) => {
    setDisabledTimes((prev) => {
      const next = new Set(prev)
      if (next.has(time)) next.delete(time)
      else next.add(time)
      return next
    })
  }

  const formatInterval = (val) => {
    if (!val) return undefined
    const predefined = INTERVAL_OPTIONS.find((o) => o.value === val)
    if (predefined) return predefined.label
    const h = Math.floor(val / 60)
    const m = val % 60
    return [h > 0 ? `${h} hr` : "", m > 0 ? `${m} min` : ""].filter(Boolean).join(" ")
  }

  const handleCustomIntervalApply = () => {
    const hours = Number(customHour) || 0
    const mins = Number(customMinute) || 0

    // Validate with an error instead of auto-clamping/rounding the typed value.
    // A field counts as "entered" only when > 0. Hour must be a whole number of
    // at least 1 (no fractional like 0.25); minutes must be a whole number ≥ 15.
    if (hours > 0 && (!Number.isInteger(hours) || hours < 1)) {
      setCustomError(t("experiences.scheduling.errors.hour_min", { defaultValue: "Hour must be a whole number of at least 1" }))
      return
    }
    if (!Number.isInteger(mins)) {
      setCustomError(t("experiences.scheduling.errors.minute_whole", { defaultValue: "Minutes must be a whole number" }))
      return
    }
    if (mins > 0 && mins < 15) {
      setCustomError(t("experiences.scheduling.errors.minute_min", { defaultValue: "Minutes must be at least 15" }))
      return
    }
    if (mins > 59) {
      setCustomError(t("experiences.scheduling.errors.minute_limit"))
      return
    }
    const total = hours * 60 + mins
    if (total <= 0) {
      setCustomError(t("form_validation.cannot_be_blank"))
      return
    }
    setInterval(total)
    setCustomError("")
    setIsIntervalOpen(false)
  }

  const intervalDropdownRender = (menu) => (
    <>
      {menu}
      <div className={styles.customIntervalContainer}>
        <Text className={styles.customIntervalLabel}>{t("global.enter_custom_duration")}</Text>
        <Flex gap={8}>
          <InputNumber
            min={0}
            step={1}
            value={customHour}
            onChange={setCustomHour}
            placeholder="hr"
            status={customError ? "error" : ""}
            className={styles.customIntervalInput}
          />
          <InputNumber
            min={0}
            step={15}
            value={customMinute}
            onChange={setCustomMinute}
            placeholder="min"
            status={customError ? "error" : ""}
            className={styles.customIntervalInput}
          />
        </Flex>
        {customError && <Text type="danger" size="sm" className="block mt-xxs">{customError}</Text>}
        <div className={styles.customIntervalApplyRow}>
          <button type="button" onClick={handleCustomIntervalApply} className={styles.customIntervalApply}>
            {t("global.apply")}
          </button>
        </div>
      </div>
    </>
  )

  const timeError = !!(fromTime && toTime && !toTime.isAfter(fromTime))
  const activeTimes = previewTimes.filter((t) => !disabledTimes.has(t))
  const selectedDayLabels = DAYS.filter(({ key }) => selectedDays.includes(key)).map(({ label }) => label).join(", ")

  const canSave = name.trim().length > 0 && selectedDays.length > 0 && activeTimes.length > 0 && !timeError

  const handleSave = () => {
    onSave({
      id: editingSchedule?.id ?? Date.now(),
      name: name.trim(),
      days: selectedDays,
      from_time: fromTime?.format("HH:mm") ?? null,
      to_time: toTime?.format("HH:mm") ?? null,
      interval,
      // start_times (enabled) is kept for local list display only — not sent to
      // the backend, which regenerates slots from the rule. disabled_times carries
      // the user's struck-out exceptions, which the rule can't derive.
      start_times: activeTimes,
      disabled_times: [...disabledTimes],
    })
    onClose()
  }

  const title = isEditing
    ? t("listings.tabs.scheduling.sections.availability_schedule.modal.edit_title")
    : t("listings.tabs.scheduling.sections.availability_schedule.modal.add_title")

  const content = (
    <>
      <Flex gap={24} className={styles.body} align="flex-start">
        {/* Left — Set Start Times */}
        <div className={styles.leftPanel}>
          <Text fw={600} size="lg" className="block mb-l">
            {t("listings.tabs.scheduling.sections.availability_schedule.modal.add_title")}
          </Text>

          {/* Name */}
          <div className="mb-l">
            <Text className="block mb-xs">
              {t("listings.tabs.scheduling.sections.availability_schedule.modal.name")}
              <span className={styles.required}>*</span>
            </Text>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Weekends"
            />
          </div>

          {/* Day selector */}
          <Text className="block mb-s">
            {t("listings.tabs.scheduling.sections.availability_schedule.modal.select_days")}
          </Text>
          <div className={styles.dayRow}>
            {DAYS.map(({ key, label }) => {
              const isSelected = selectedDays.includes(key)
              const isTaken = takenDays.has(key)
              const dayButton = (
                <button
                  key={key}
                  type="button"
                  aria-disabled={isTaken || undefined}
                  onClick={() => handleDayToggle(key)}
                  className={`${styles.dayButton} ${isSelected ? styles.dayButtonSelected : ""} ${isTaken ? styles.dayButtonDisabled : ""}`}
                >
                  {label}
                </button>
              )
              return isTaken ? (
                <Tooltip
                  key={key}
                  title={t("listings.tabs.scheduling.sections.availability_schedule.modal.day_taken", { defaultValue: "Already used in another schedule" })}
                >
                  {dayButton}
                </Tooltip>
              ) : (
                dayButton
              )
            })}
          </div>

          {/* From / To */}
          <Flex gap={16} className="mb-l mt-l">
            <div className={styles.timeField}>
              <Text className="block mb-xs">
                {t("listings.tabs.scheduling.sections.availability_schedule.modal.from")}
                <span className={styles.required}>*</span>
              </Text>
              <TimePicker
                needConfirm={false}
                use12Hours
                format="hh:mm A"
                value={fromTime}
                onChange={setFromTime}
                className={styles.timePicker}
              />
            </div>
            <div className={styles.timeField}>
              <Text className="block mb-xs">
                {t("listings.tabs.scheduling.sections.availability_schedule.modal.to")}
                <span className={styles.required}>*</span>
              </Text>
              <TimePicker
                needConfirm={false}
                use12Hours
                format="hh:mm A"
                value={toTime}
                onChange={setToTime}
                className={styles.timePicker}
                status={timeError ? "error" : ""}
              />
              {timeError && (
                <Text type="danger" size="sm" className="block mt-xxs">
                  {t("listings.tabs.scheduling.sections.availability_schedule.modal.to_after_from")}
                </Text>
              )}
            </div>
          </Flex>

          {/* Interval */}
          <div>
            <Text className="block mb-xs">
              {t("listings.tabs.scheduling.sections.availability_schedule.modal.interval")}
            </Text>
            <Select
              open={isIntervalOpen}
              onDropdownVisibleChange={setIsIntervalOpen}
              value={formatInterval(interval)}
              onSelect={(val) => { setInterval(val); setIsIntervalOpen(false) }}
              placeholder="Select"
              className={styles.intervalSelect}
              dropdownRender={intervalDropdownRender}
            >
              {INTERVAL_OPTIONS.map((o) => (
                <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
              ))}
            </Select>
          </div>
        </div>

        {/* Right — Preview */}
        <div className={styles.rightPanel}>
          <Text fw={600} size="lg" className="block mb-m">
            {t("listings.tabs.scheduling.sections.availability_schedule.modal.preview_title")}
          </Text>

          {previewTimes.length === 0 ? (
            <Text type="secondary" className={styles.noPreview}>
              {t("listings.tabs.scheduling.sections.availability_schedule.modal.no_preview")}
            </Text>
          ) : (
            <>
              <div className={styles.previewInfo}>
                <InfoCircleOutlined className={styles.previewInfoIcon} />
                <Text size="sm">
                  {t("listings.tabs.scheduling.sections.availability_schedule.modal.preview_info")}
                </Text>
              </div>
              {selectedDayLabels && (
                <Text fw={700} className="block mb-s mt-m">
                  {selectedDayLabels}
                </Text>
              )}
              <Flex gap={6} wrap="wrap" className={styles.timeChipList}>
                {previewTimes.map((time) => {
                  const isDisabled = disabledTimes.has(time)
                  return (
                    <button
                      key={time}
                      type="button"
                      onClick={() => handleToggleTime(time)}
                      className={`${styles.timeChip} ${isDisabled ? styles.timeChipDisabled : styles.timeChipActive}`}
                    >
                      {time}
                    </button>
                  )
                })}
              </Flex>
            </>
          )}
        </div>
      </Flex>

      {/* Footer */}
      <Flex gap={12} className={styles.footer}>
        <Button shape="round" onClick={onClose}>{"Cancel"}</Button>
        <Button type="primary" shape="round" disabled={!canSave} onClick={handleSave}>
          {isEditing
            ? t("listings.tabs.scheduling.sections.availability_schedule.modal.update")
            : t("listings.tabs.scheduling.sections.availability_schedule.modal.save")}
        </Button>
      </Flex>
    </>
  )

  if (isMobile) {
    return (
      <CustomDrawer open={open} setOpen={() => onClose()} title={title}>
        {content}
      </CustomDrawer>
    )
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      width={900}
      footer={null}
      destroyOnClose
    >
      {content}
    </Modal>
  )
}

AddAvailabilityScheduleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  existingSchedules: PropTypes.array,
  editingSchedule: PropTypes.object,
}
